import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import api, { formatPrice } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [order, setOrder] = useState(null);
  const { setBag } = useStore();
  const tries = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setState("paid"); setOrder(data.order);
          if (!clearedRef.current) { localStorage.removeItem("nba_bag"); setBag && setBag([]); clearedRef.current = true; }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
        if (tries.current++ < 12) setTimeout(poll, 2000); else setState("failed");
      } catch { if (tries.current++ < 12) setTimeout(poll, 2000); else setState("failed"); }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, setBag]);

  if (state === "checking") return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
      <Loader2 className="animate-spin mb-4 text-gold" />
      <p className="font-serif text-3xl">Confirming your order…</p>
    </div>
  );

  if (state === "failed") return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
      <p className="font-serif text-5xl mb-4">Payment not completed.</p>
      <p className="text-espresso/60 mb-8">If you were charged, contact us and we'll put it right.</p>
      <Link to="/checkout" className="border border-espresso px-8 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase hover:bg-espresso hover:text-cream transition-colors">Try Again</Link>
    </div>
  );

  return (
    <div className="min-h-[70vh] max-w-xl mx-auto text-center px-6 pt-20 pb-24">
      <CheckCircle2 className="mx-auto mb-6 text-sage" size={40} strokeWidth={1.4} />
      <p className="overline text-gold mb-3">Order Confirmed</p>
      <h1 className="font-serif text-6xl md:text-7xl leading-none mb-6">She's yours.</h1>
      {order && (
        <div className="bg-sand p-6 text-left mt-8">
          <div className="flex justify-between text-sm mb-4 pb-4 border-b border-espresso/10">
            <span className="text-espresso/60">Order</span><span className="font-medium">{order.order_number}</span>
          </div>
          <div className="flex justify-between text-sm mb-4 pb-4 border-b border-espresso/10">
            <span className="text-espresso/60">Email</span><span>{order.email}</span>
          </div>
          {order.items.map((i, x) => (
            <div key={x} className="flex justify-between text-sm py-1.5">
              <span>{i.name} · {i.colour}/{i.size} × {i.qty}</span><span>{formatPrice(i.price * i.qty)}</span>
            </div>
          ))}
          {order.discount_amount > 0 && (
            <div className="flex justify-between text-sm mt-3 pt-3 border-t border-espresso/10 text-sage">
              <span>Discount{order.discount_code ? ` (${order.discount_code})` : ""}</span><span>−{formatPrice(order.discount_amount)}</span>
            </div>
          )}
          <div className="flex justify-between text-base mt-4 pt-4 border-t border-espresso/10">
            <span>Total</span><span>{formatPrice(order.total)}</span>
          </div>
        </div>
      )}
      <p className="text-espresso/60 text-sm mt-8">A confirmation has been sent to your email. We'll be in touch when she's on her way.</p>
      <Link to="/" className="inline-block mt-8 text-[0.72rem] tracking-[0.2em] uppercase hover-underline">Continue exploring</Link>
    </div>
  );
};

export default PaymentSuccess;
